"use client";

import { useState } from "react";
import { motion } from "motion/react";
import { ArrowRight, Check, Clock, Copy, Mail, Phone, Rocket } from "lucide-react";
import { useLanguage } from "@/components/marketing/LanguageContext";
import { getSection02Data } from "./data";

function EarlyLaunchCard() {
  const { language } = useLanguage();
  const data = getSection02Data(language);

  return (
    <div className="relative h-full rounded-[10px] p-[1.5px] overflow-hidden">
      <motion.div
        aria-hidden
        animate={{ rotate: 360 }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        className="absolute -inset-[200%]"
        style={{ background: "conic-gradient(from 0deg, transparent 0 60%, #C99A2E 80%, #E2C068 90%, transparent 100%)" }}
      />
      <div className="relative h-full rounded-[9px] bg-white/95 backdrop-blur-xl p-3.5 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="relative shrink-0">
            <motion.span
              aria-hidden
              animate={{ scale: [1, 1.7], opacity: [0.5, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
              className="absolute inset-0 rounded-full bg-[#C99A2E]"
            />
            <div className="relative w-9 h-9 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-[#0B1D3A] to-[#1E3A6E] shadow-md">
              <motion.div
                animate={{ y: [0, -3, 0], rotate: [0, -6, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              >
                <Rocket size={16} className="text-white" />
              </motion.div>
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[#C99A2E]">{data.offer.tag}</span>
              <motion.span
                animate={{ opacity: [1, 0.2, 1] }}
                transition={{ duration: 1.4, repeat: Infinity }}
                className="w-1.5 h-1.5 rounded-full bg-[#C99A2E]"
              />
            </div>
            <p className="text-[12.5px] font-semibold text-[#0B1D3A] leading-snug">{data.offer.title}</p>
          </div>
        </div>
        <a
          href={`mailto:${data.contact.email}?subject=Early%20Launch%20Inquiry%20-%20FARE`}
          className="mt-auto w-full text-white text-[12.5px] font-bold px-3 py-2.5 rounded-[8px] flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#0B1D3A] to-[#1E3A6E] hover:from-[#15315C] hover:to-[#0B1D3A] shadow-md hover:shadow-lg transition-all"
        >
          <span>{data.offer.cta}</span>
          <ArrowRight size={14} strokeWidth={2.5} className="text-white" />
        </a>
      </div>
    </div>
  );
}

function ContactCard() {
  const { language } = useLanguage();
  const data = getSection02Data(language);
  const [copiedType, setCopiedType] = useState<"mail" | "phone" | null>(null);

  const handleCopy = (text: string, type: "mail" | "phone") => {
    void navigator.clipboard.writeText(text);
    setCopiedType(type);
    setTimeout(() => setCopiedType(null), 2000);
  };

  const rows = [
    {
      type: "mail" as const,
      value: data.contact.email,
      href: `mailto:${data.contact.email}?subject=FARE%20Early%20Inquiry`,
      label: `Send email to ${data.contact.email}`,
      copyLabel: "Copy email address",
      icon: <Mail size={15} strokeWidth={2.4} className="text-white" />,
      color: "#C99A2E",
    },
    {
      type: "phone" as const,
      value: data.contact.phone,
      href: `tel:${data.contact.phone.replace(/\s+/g, "")}`,
      label: `Call ${data.contact.phone}`,
      copyLabel: "Copy phone number",
      icon: <Phone size={15} strokeWidth={2.4} className="text-white" />,
      color: "#2563EB",
    },
  ];

  return (
    <div className="h-full rounded-[10px] border border-slate-200 bg-white/90 backdrop-blur-xl p-3.5 flex flex-col gap-2">
      <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#64748B]">{data.contact.title}</span>
      {rows.map((row) => (
        <div
          key={row.type}
          className="relative flex items-center justify-between bg-white hover:bg-slate-50 active:bg-slate-100/80 px-2 py-1.5 rounded-lg border border-slate-200 shadow-xs transition-all group"
        >
          <a href={row.href} className="flex items-center gap-2.5 flex-1 min-w-0 after:absolute after:inset-0" aria-label={row.label}>
            <div
              className="w-8 h-8 rounded-lg text-white flex items-center justify-center shrink-0 shadow-xs group-hover:scale-105 transition-transform"
              style={{ background: row.color }}
            >
              {row.icon}
            </div>
            <span className="text-[12px] font-bold text-[#0B1D3A] truncate">{row.value}</span>
          </a>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCopy(row.value, row.type);
            }}
            className="relative z-10 p-1.5 rounded-md text-slate-400 hover:text-[#0B1D3A] hover:bg-slate-50 active:bg-slate-200 transition-colors shrink-0 cursor-pointer"
            title={row.copyLabel}
            aria-label={row.copyLabel}
          >
            {copiedType === row.type ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
          </button>
        </div>
      ))}
      <div className="text-[10.5px] text-[#64748B] flex items-center gap-2 pt-1">
        <span className="w-4 h-4 rounded-full bg-[#0B1D3A] text-white flex items-center justify-center shrink-0">
          <Clock size={9} strokeWidth={2.4} className="text-white" />
        </span>
        <span className="leading-snug">{data.contact.hours}</span>
      </div>
    </div>
  );
}

export default function EarlyAccess({ mobile = false }: { mobile?: boolean }) {
  return (
    <section
      className={`w-full relative font-['Outfit'] ${mobile ? "px-4 py-5" : "px-6 sm:px-10 xl:px-14 py-8"}`}
      style={{ background: "linear-gradient(180deg, #F4F7FB 0%, #FFFFFF 100%)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className={`mx-auto grid gap-3 items-stretch ${mobile ? "grid-cols-1 sm:grid-cols-2" : "max-w-[960px] grid-cols-2 gap-5"}`}
      >
        <EarlyLaunchCard />
        <ContactCard />
      </motion.div>
    </section>
  );
}
